import React, { useState, useEffect, useRef } from 'react';
import { ButtonBase, IconButton, Tooltip, Box, Typography } from '@mui/material';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import type { TypographyProps } from '@mui/material/Typography';
import type { IconButtonProps } from '@mui/material/IconButton';
import type { TooltipProps } from '@mui/material/Tooltip';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { keyframes } from '@mui/system';

// Animation keyframes for radiating out from center
const radiateOut = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0.8;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
`;

// Animation keyframes for collapsing back to center
const collapseIn = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0.8;
  }
`;

// Icon pop animation for liked state
const iconPop = keyframes`
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(0.8);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

/**
 * Props for the LikeButton component.
 */
export interface LikeButtonProps {
  /** Animation duration in milliseconds */
  animationDuration?: number;
  /** Props passed to the MUI IconButton */
  IconButtonProps?: Omit<IconButtonProps, 'onClick' | 'children'>;
  /** Controlled liked state */
  liked?: boolean;
  /** Custom liked icon component */
  LikedIcon?: React.ElementType;
  /** Props passed to the liked (filled) icon */
  LikedIconProps?: SvgIconProps;
  /** Tooltip for liked state (defaults to "Unlike") */
  likedTooltip?: string;
  /** Callback when like is toggled */
  onToggle?: (liked: boolean, event?: React.MouseEvent) => void;
  /** Color for the radiate effect (defaults to liked icon color) */
  radiateColor?: string;
  /** Whether to show the tooltip */
  showTooltip?: boolean;
  /** Tooltip text (defaults to "Like"/"Unlike" based on state) */
  tooltip?: string;
  /** Props passed to the MUI Tooltip */
  TooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
  /** Custom unliked icon component */
  UnlikedIcon?: React.ElementType;
  /** Props passed to the unliked (outline) icon */
  UnlikedIconProps?: SvgIconProps;
  /** Tooltip for unliked state (defaults to "Like") */
  unlikedTooltip?: string;
  /** Toggle behavior for button clicks */
  toggleMode?: 'toggle' | 'action';
  /** Optional count displayed next to the button */
  count?: number;
  /** Show count even when it is zero */
  showZeroCount?: boolean;
  /** Props passed to the count button */
  CountButtonProps?: Omit<ButtonBaseProps, 'onClick' | 'children'>;
  /** Props passed to the count text */
  CountTypographyProps?: TypographyProps;
}

/**
 * LikeButton component allows users to toggle a like/favorite state.
 *
 * Supports both controlled and uncontrolled modes, customizable icons,
 * and full MUI prop passthrough for IconButton, Tooltip, and icons.
 *
 * Features a radiate-out animation when liked and collapse-in when unliked.
 */
const LikeButton: React.FC<LikeButtonProps> = ({
  animationDuration = 400,
  IconButtonProps = {},
  liked,
  LikedIcon = FavoriteIcon,
  LikedIconProps = {},
  likedTooltip = 'Unlike',
  onToggle,
  radiateColor = '#c2185b',
  showTooltip = true,
  tooltip,
  TooltipProps = {},
  UnlikedIcon = FavoriteBorderIcon,
  UnlikedIconProps = {},
  unlikedTooltip = 'Like',
  toggleMode = 'toggle',
  count,
  showZeroCount = false,
  CountButtonProps = {},
  CountTypographyProps = {},
}) => {
  const [internalLiked, setInternalLiked] = useState<boolean>(false);
  const [animationState, setAnimationState] = useState<'idle' | 'radiating' | 'collapsing'>('idle');

  // Determine the actual liked state (controlled vs uncontrolled)
  const isControlled = liked !== undefined;
  const currentLiked = isControlled ? liked : internalLiked;

  // Animation timer — kept in a ref so rapid clicks cancel the previous timer
  // instead of queuing multiple `setAnimationState('idle')` calls.
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    };
  }, []);

  // Animation is triggered directly in handleToggle rather than in a
  // useEffect that watches `currentLiked`. This is deliberate: when the same
  // controlled `liked` value is shared across multiple LikeButton instances
  // (e.g. top and bottom engagement bars on the reader page), a useEffect
  // would fire in every instance whenever the prop changes, making ALL buttons
  // animate. Triggering here means only the instance the user clicked animates.
  const triggerAnimation = (willLike: boolean) => {
    if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    setAnimationState(willLike ? 'radiating' : 'collapsing');
    animationTimerRef.current = setTimeout(() => setAnimationState('idle'), animationDuration);
  };

  // Determine tooltip text
  const tooltipText = tooltip ?? (currentLiked ? likedTooltip : unlikedTooltip);

  const handleToggle = (event?: React.MouseEvent) => {
    if (toggleMode === 'action') {
      onToggle?.(currentLiked, event);
      return;
    }

    const newLiked = !currentLiked;
    triggerAnimation(newLiked);

    if (!isControlled) {
      setInternalLiked(newLiked);
    }

    onToggle?.(newLiked, event);
  };

  // Default icon props - using darker colors for AAA compliance on light backgrounds
  const defaultLikedIconProps: SvgIconProps = {
    sx: {
      color: '#c2185b',
      animation:
        animationState === 'radiating' ? `${iconPop} ${animationDuration}ms ease-out` : 'none',
    },
    ...LikedIconProps,
  };

  const defaultUnlikedIconProps: SvgIconProps = {
    sx: {
      color: '#1a1a1a',
      transition: `transform ${animationDuration / 2}ms ease-out`,
    },
    ...UnlikedIconProps,
  };

  // Extract positioning styles to apply to wrapper, keep other styles on IconButton
  const positioningKeys = [
    'position',
    'top',
    'right',
    'bottom',
    'left',
    'zIndex',
    'transform',
    'margin',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'm',
    'mt',
    'mr',
    'mb',
    'ml',
    'mx',
    'my',
  ];

  const iconButtonSx = IconButtonProps.sx as Record<string, unknown> | undefined;
  const wrapperSx: Record<string, unknown> = {};
  const buttonSx: Record<string, unknown> = {};

  if (iconButtonSx) {
    Object.entries(iconButtonSx).forEach(([key, value]) => {
      if (positioningKeys.includes(key)) {
        wrapperSx[key] = value;
      } else {
        buttonSx[key] = value;
      }
    });
  }

  const showCount = typeof count === 'number' && (count > 0 || showZeroCount);

  const iconButton = (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        isolation: 'isolate',
        ...wrapperSx,
      }}
    >
      {/* Radiate/Collapse animation overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          backgroundColor: radiateColor,
          transform: 'translate(-50%, -50%) scale(0)',
          pointerEvents: 'none',
          zIndex: -1,
          ...(animationState === 'radiating' && {
            animation: `${radiateOut} ${animationDuration}ms ease-out forwards`,
          }),
          ...(animationState === 'collapsing' && {
            animation: `${collapseIn} ${animationDuration}ms ease-out forwards`,
          }),
        }}
      />
      <IconButton
        aria-label={currentLiked ? 'Remove from favorites' : 'Add to favorites'}
        {...IconButtonProps}
        onClick={handleToggle}
        disableRipple
        disableTouchRipple
        sx={{
          position: 'relative',
          overflow: 'visible',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          ...buttonSx,
        }}
      >
        {currentLiked ? (
          <LikedIcon {...defaultLikedIconProps} />
        ) : (
          <UnlikedIcon {...defaultUnlikedIconProps} />
        )}
      </IconButton>
    </Box>
  );

  const iconButtonWithCount = (
    <ButtonBase
      onClick={handleToggle}
      aria-label={currentLiked ? 'Remove from favorites' : 'Add to favorites'}
      disableRipple
      disableTouchRipple
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'transparent',
        },
        ...wrapperSx,
      }}
      {...CountButtonProps}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          isolation: 'isolate',
        }}
      >
        {/* Radiate/Collapse animation overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            backgroundColor: radiateColor,
            transform: 'translate(-50%, -50%) scale(0)',
            pointerEvents: 'none',
            zIndex: -1,
            ...(animationState === 'radiating' && {
              animation: `${radiateOut} ${animationDuration}ms ease-out forwards`,
            }),
            ...(animationState === 'collapsing' && {
              animation: `${collapseIn} ${animationDuration}ms ease-out forwards`,
            }),
          }}
        />
        <IconButton
          {...IconButtonProps}
          component="span"
          disableRipple
          disableTouchRipple
          sx={{
            position: 'relative',
            overflow: 'visible',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            ...buttonSx,
          }}
        >
          {currentLiked ? (
            <LikedIcon {...defaultLikedIconProps} />
          ) : (
            <UnlikedIcon {...defaultUnlikedIconProps} />
          )}
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.primary" {...CountTypographyProps}>
        {count}
      </Typography>
    </ButtonBase>
  );

  const buttonNode = showCount ? iconButtonWithCount : iconButton;

  if (!showTooltip) {
    return buttonNode;
  }

  return (
    <Tooltip title={tooltipText} {...TooltipProps}>
      {buttonNode}
    </Tooltip>
  );
};

export default LikeButton;
